import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

export interface DiarySubjectFilter {
  subjectId: number;
  subject: string;
}

export interface GetSubjectFilterResponse {
  status: boolean;
  body: {
    subjectListForFilter: DiarySubjectFilter[];
  };
}

export interface DiaryEntry {
  diaryId: string;
  subjectId: string;
  subject: string;
  HomeworkNumber: string;
  diaryTitle: string | null;
  isAttachmentWithDiary: boolean;
  staffImage: string;
  staffName: string;
  homeworkDonePercentage: number;
  dueDate: string;
}

export interface GetTodaysDiaryResponse {
  status: boolean;
  body: {
    diaryDetailsForTodaysDueDates: DiaryEntry[];
    isStaffAllowedToCreateDiary: boolean;
    academicStartDate: string;
    academicEndDate: string;
    maxPageNumber: number;
  };
}

export interface GetPastDiaryResponse {
  status: boolean;
  body: {
    diaryDetailsForPastDueDates: DiaryEntry[];
    isStaffAllowedToCreateDiary: boolean;
    academicStartDate: string;
    academicEndDate: string;
    maxPageNumber: number;
  };
}

export interface GetUpcomingDiaryResponse {
  status: boolean;
  body: {
    diaryDetailsForUpcomingDueDates: DiaryEntry[];
    isStaffAllowedToCreateDiary: boolean;
    academicStartDate: string;
    academicEndDate: string;
    maxPageNumber: number;
  };
}

export interface DiaryDetailItem {
  diaryTitle: string;
  diaryDescription: string | null;
  diaryDuration: number | null;
  submissionDate: string;
  homeworkDonePercentage: number;
}

export interface DiaryMediaItem {
  DiaryMediaType: 'IMAGE' | 'DOCUMENT';
  DiaryMediaUrl: string;
  DiaryMediaBlurUrl: string;
}

export interface DiaryStudentStatus {
  studentImage: string;
  studentName: string;
  rollNumber: string;
  classNumber: string;
  classDivision: string;
  classTeacherName: string;
}

export interface DiaryDetailData {
  diaryDetails: DiaryDetailItem[];
  diaryMediaDetails: DiaryMediaItem[];
  diaryNotCompletedStudentDetails: DiaryStudentStatus[];
  diaryCompletedStudentDetails: DiaryStudentStatus[];
  isStaffAllowedToEditDiary: boolean;
}

export interface GetDiaryDetailsResponse {
  status: boolean;
  body: {
    diaryData: DiaryDetailData;
  };
}

export interface CreateDiarySubject {
  subjectId: number;
  subject: string;
}

export interface GetSubjectForCreateDiaryResponse {
  status: boolean;
  body: {
    subjectListForCreateDiaryData: CreateDiarySubject[];
  };
}

export interface DiaryReplicateClass {
  classNumber: string;
  classDivision: string;
  teachingClassId: number;
}

export interface GetClassListForCreateDiaryResponse {
  status: boolean;
  body: {
    classListForReplicatingDiaryData: DiaryReplicateClass[];
  };
}

export interface CreateDiaryFields {
  staffId: string;
  subjectId: string;
  diaryTitle: string;
  diaryDescription: string;
  diaryEstimateTime: number;
  diarySubmissionDate: string;
  classIdList: string[];
  lang: string;
}

export interface EditDiaryFields {
  diaryId: string;
  staffId: string;
  subjectId: string;
  diaryTitle: string;
  diaryDescription: string;
  deletedMediaIdList: string[];
  diaryEstimateTime: number;
  diarySubmissionDate: string;
  classId: string;
  lang: string;
}

export interface SimpleStatusResponse {
  status: boolean;
  body?: { message?: string };
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class StaffDiaryService {
  private url = AppSettings.API_ENDPOINT_STAFF;
  private getSubjectForFilterDiaryEndpoint = apiConstants.getSubjectForFilterDiary;
  private getTodaysDiaryDataEndpoint = apiConstants.getTodaysDiaryData;
  private getPastDiaryDataEndpoint = apiConstants.getPastDiaryData;
  private getUpcomingDiaryDataEndpoint = apiConstants.getUpcomingDiaryData;
  private getDiaryDetailsEndpoint = apiConstants.getDiaryDetails;
  private getSubjectForCreateDiaryEndpoint = apiConstants.getSubjectForCreateDiary;
  private getClassListForCreateDiaryEndpoint = apiConstants.getClassListForCreateDiary;
  private createDiaryEndpoint = apiConstants.createDiaryForStaff;
  private editDiaryEndpoint = apiConstants.editDiaryForStaff;

  constructor(private http: HttpClient) {}

  getSubjectFilterList(classId: number | string, staffId: number | string) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('lang', 'en')
      .set('staffId', staffId);

    return this.http
      .get<GetSubjectFilterResponse>(`${this.url}${this.getSubjectForFilterDiaryEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  // Today has no diaryDate concept — never sent.
  getTodaysDiaryData(classId: number | string, staffId: number | string, pageNumber: number, subjectId: number | null) {
    const body = {
      classId: String(classId),
      staffId: String(staffId),
      pageNumber,
      subjectId,
      lang: 'en',
    };
    return this.http
      .post<GetTodaysDiaryResponse>(`${this.url}${this.getTodaysDiaryDataEndpoint}`, body)
      .pipe(map((res) => res));
  }

  // diaryDate is "ALL" or a "YYYY-MM-DD" string.
  getPastDiaryData(classId: number | string, staffId: number | string, pageNumber: number, diaryDate: string, subjectId: number | null) {
    const body = {
      classId: String(classId),
      staffId: String(staffId),
      pageNumber,
      diaryDate,
      subjectId,
      lang: 'en',
    };
    return this.http
      .post<GetPastDiaryResponse>(`${this.url}${this.getPastDiaryDataEndpoint}`, body)
      .pipe(map((res) => res));
  }

  // diaryDate is "ALL" or a "YYYY-MM-DD" string.
  getUpcomingDiaryData(classId: number | string, staffId: number | string, pageNumber: number, diaryDate: string, subjectId: number | null) {
    const body = {
      classId: String(classId),
      staffId: String(staffId),
      pageNumber,
      diaryDate,
      subjectId,
      lang: 'en',
    };
    return this.http
      .post<GetUpcomingDiaryResponse>(`${this.url}${this.getUpcomingDiaryDataEndpoint}`, body)
      .pipe(map((res) => res));
  }

  getDiaryDetails(classId: number | string, diaryId: string, subjectId: number | string, staffId: number | string) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('diaryId', diaryId)
      .set('subjectId', subjectId)
      .set('lang', 'en')
      .set('staffId', staffId);

    return this.http
      .get<GetDiaryDetailsResponse>(`${this.url}${this.getDiaryDetailsEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  getSubjectListForCreateDiary(classId: number | string, staffId: number | string) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('lang', 'en')
      .set('staffId', staffId);

    return this.http
      .get<GetSubjectForCreateDiaryResponse>(`${this.url}${this.getSubjectForCreateDiaryEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  getClassListForDiary(classId: number | string, subjectId: number | string, staffId: number | string) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('subjectId', subjectId)
      .set('lang', 'en')
      .set('staffId', staffId);

    return this.http
      .get<GetClassListForCreateDiaryResponse>(`${this.url}${this.getClassListForCreateDiaryEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  createDiary(fields: CreateDiaryFields, mediaFiles: File[]) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      // Sent as a single comma-separated field (e.g. "598,601"), not one
      // classIdList[] part per entry.
      formData.append(key, Array.isArray(value) ? value.join(',') : String(value));
    });
    mediaFiles.forEach(file => formData.append('diaryMedia', file));

    return this.http
      .post<SimpleStatusResponse>(`${this.url}${this.createDiaryEndpoint}`, formData)
      .pipe(map((res) => res));
  }

  editDiary(fields: EditDiaryFields, mediaFiles: File[]) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      // Sent as a single comma-separated field, matching classIdList's format.
      formData.append(key, Array.isArray(value) ? value.join(',') : String(value));
    });
    mediaFiles.forEach(file => formData.append('diaryMedia', file));

    return this.http
      .put<SimpleStatusResponse>(`${this.url}${this.editDiaryEndpoint}`, formData)
      .pipe(map((res) => res));
  }
}
